import { AlertTriangle } from "lucide-react";
import { Tile, Row } from "./Tile";
import { duration, clock } from "../../lib/dash";

const USES = { GEMINI_IMAGE_MODEL: "images", GEMINI_IMAGE_STRONG_MODEL: "fallback", GEMINI_SEG_MODEL: "colour split", GEMINI_SPEC_MODEL: "feature spec" };

// One entry per model id that a setting resolves to and that is listed with an upstream retirement date.
export const retiredModels = (models = []) => Object.values(models.filter((m) => m.retired_on).reduce((acc, m) => {
  (acc[m.value] ||= { id: m.value, on: m.retired_on, uses: [] }).uses.push(USES[m.name] || m.name);
  return acc;
}, {}));

const Models = ({ models }) => {
  const retired = retiredModels(models);
  if (!retired.length) return <Row dense label="AI models" testid="web-models" value="none listed as retired" />;
  const dates = [...new Set(retired.map((m) => m.on))].join(", ");
  return (
    <div data-testid="web-models" className="mt-1 rounded-lg border border-amber-300 bg-amber-50/60 px-2 py-1 text-[11.5px] leading-snug">
      {retired.map((m) => (
        <p key={m.id} data-testid={`web-model-${m.id}`} className="flex items-center gap-1 py-0.5">
          <AlertTriangle className="h-3 w-3 shrink-0 text-amber-700" />
          <span className="truncate font-mono2 font-semibold text-amber-800">{m.id}</span>
          <span className="shrink-0 text-muted-foreground">· {m.uses.join(", ")}</span>
        </p>
      ))}
      <p data-testid="web-models-note" className="text-muted-foreground">
        Listed as retired {dates} (unverified) · still served by the Universal Key
      </p>
    </div>
  );
};

export const WebsiteTile = ({ w, models, tone, index }) => (
  <Tile id="website" index={index} tone={tone} kicker="Tile 3 · this server" title="Website">
    <div data-testid="web-rows" className="dash-scroll min-h-0 flex-1 overflow-y-auto pr-1">
      <Row dense label="Health" testid="web-health" value={`${w.health === "ok" ? "OK" : w.health} · up ${duration(w.uptime_seconds)} (${clock(w.started_at)})`} />
      <Row dense label="Restarts (7 days)" testid="web-restarts" value={w.restarts_7d} />
      <Row dense label="Requests · 5xx (60 / 15 min)" testid="web-errors" tone={w.errors_15m ? "red" : undefined}
        value={`${w.requests_60m} · ${w.errors_60m} / ${w.errors_15m}`} />
      <Row dense label="Heavy worker" testid="web-heavy" tone={w.heavy_failures_24h ? "red" : undefined}
        value={`${w.heavy_busy ? "busy" : "idle"} · ${w.export_queue} queued · ${w.heavy_failures_24h} failed (24 h)`} />
      <Row dense label="Build · backend" testid="web-build"
        value={`${w.version?.build || "unknown"} · ${(w.version?.backend_sha || "").slice(0, 12) || "unknown"}`} />
      <Row dense label="Spend gate" value={`${w.spend_gate?.mode || "?"}${w.spend_gate?.paused ? " · paused" : ""} · ${w.spend_gate?.environment || ""}`} />
      <Row dense label="Log lines stored / dropped" value={`${w.log_writer?.written ?? 0} / ${w.log_writer?.dropped ?? 0}`} />
      <Models models={models} />
    </div>
  </Tile>
);
